import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import api from '../../services/api';
import { PressToTalkButton } from '../../components/common/PressToTalkButton';
import { useAudioTranscription } from '../../hooks/useAudioTranscription';
import { APP_CONFIG } from '../../config/appConfig';

interface Badge {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  criteria: any;
}

interface AiFeedback {
  passed: boolean;
  score: number;
  strengths: string[];
  improvements: string[];
  examples: Array<{ quote: string; comment: string }>;
  encouragement: string;
}

const AI_NAME = APP_CONFIG.aiName;

export const WritingWorkspace: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  
  const [content, setContent] = useState('');
  const [badges, setBadges] = useState<Badge[]>([]);
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<AiFeedback | null>(null);
  const [earnedBadges, setEarnedBadges] = useState<string[]>([]);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [loadError, setLoadError] = useState('');
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    status: transcriptionStatus,
    errorMessage: transcriptionError,
    isSupported: isSpeechSupported,
    startRecording,
    stopRecording,
    cancelRecording,
  } = useAudioTranscription({
    context: 'writing-main',
    onTranscription: (text) => {
      setContent((prev) => prev + text);
    },
  });

  useEffect(() => {
    let active = true;

    const loadAssignment = async () => {
      if (!assignmentId) {
        setLoadError('缺少任务 ID');
        return;
      }

      try {
        const response = await api.get(`/assignments/${assignmentId}`);
        if (!active) return;
        const assignment = response.data;
        const nextBadges = Array.isArray(assignment.badges) ? assignment.badges : [];
        setBadges(nextBadges);
        setSelectedBadge(nextBadges[0] ?? null);
      } catch (error) {
        if (active) {
          setLoadError('加载任务失败，请返回重试');
        }
      }
    };

    loadAssignment();

    return () => {
      active = false;
    };
  }, [assignmentId]);

  // OCR 处理
  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsRecognizing(true);
    const formData = new FormData();
    formData.append('image', file);

    try {
      const response = await api.post('/ocr/recognize', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      const text = response.data.text;
      if (text) {
        setContent((prev) => (prev ? prev + '\n' + text : text));
      }
    } catch (error) {
      console.error('OCR failed:', error);
      alert('图片识别失败，请重试');
    } finally {
      setIsRecognizing(false);
      // 清空 input 允许重复上传同一文件
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!content.trim() || isSubmitting) return;
    if (!assignmentId || !selectedBadge) return;
    
    setIsSubmitting(true);
    setFeedback(null);

    try {
      const realAssignmentId = assignmentId;
      const realBadgeId = selectedBadge.id;

      const response = await api.post('/submissions', {
        assignmentId: realAssignmentId,
        badgeId: realBadgeId,
        content,
      });

      const { aiFeedback, badgeUnlocked } = response.data;
      setFeedback(aiFeedback);

      if (badgeUnlocked) {
        setEarnedBadges((prev) => [...prev, selectedBadge.id]);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 5000);
      }
    } catch (error) {
      console.error('Submission failed:', error);
      alert('提交失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row md:h-screen md:overflow-hidden">
      {showConfetti && <Confetti numberOfPieces={200} recycle={false} />}

      {/* 左侧：写作区域 */}
      <div className="flex-1 flex flex-col p-4 md:p-8 md:overflow-y-auto">
        <header className="flex justify-between items-center mb-6">
          <button 
            onClick={() => navigate('/student')}
            className="text-gray-500 hover:text-gray-700 flex items-center"
          >
            ← 返回任务列表
          </button>
          <div className="text-sm text-gray-400">
            {content.length} 字
          </div>
        </header>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 relative flex flex-col min-h-[50vh] md:min-h-0 md:flex-1">
          {/* 工具栏 */}
          <div className="flex items-center space-x-2 mb-4 pb-4 border-b border-gray-100">
             <input 
               type="file" 
               accept="image/*" 
               capture="environment" // 在移动设备上优先调用后置摄像头
               ref={fileInputRef} 
               onChange={handleFileChange} 
               className="hidden" 
             />
             
             <button
               onClick={handleCameraClick}
               disabled={isRecognizing}
               className={`p-2 rounded-lg flex items-center space-x-2 text-sm font-medium transition-colors ${
                 isRecognizing ? 'bg-gray-100 text-gray-400' : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
               }`}
               title="拍照识别文字"
             >
               <span>{isRecognizing ? '识别中...' : '📷 拍照识别'}</span>
             </button>

             <PressToTalkButton
               disabled={!isSpeechSupported}
               status={transcriptionStatus}
               onStart={startRecording}
               onStop={stopRecording}
             />
          </div>

          {transcriptionError ? (
            <div className="mb-3 text-sm text-red-500">{transcriptionError}</div>
          ) : null}

          <textarea
            className="flex-1 w-full resize-none border-none focus:ring-0 text-lg leading-relaxed text-gray-800 placeholder-gray-300"
            placeholder="在这里开始你的故事... (支持手写拍照识别或语音输入)"
            value={content}
            onChange={(e) => {
              if (transcriptionStatus === 'recording') {
                cancelRecording();
              }
              setContent(e.target.value);
            }}
            disabled={isSubmitting}
          />
          
          <div className="absolute bottom-6 right-6">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || content.trim().length < 10 || !selectedBadge}
              className={`px-6 py-3 rounded-full font-bold shadow-lg transition-all transform hover:scale-105 flex items-center space-x-2 ${
                isSubmitting || content.trim().length < 10 || !selectedBadge
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>{AI_NAME}思考中...</span>
                </>
              ) : (
                <span>提交评估 ✨</span>
              )}
            </button>
            {content.trim().length < 10 && content.trim().length > 0 && (
                <div className="text-xs text-red-400 mt-2 text-center absolute top-full w-full">
                    再写一点点 (至少10个字)
                </div>
            )}
          </div>
        </div>
      </div>

      {/* 右侧：勋章与反馈 */}
      <div className="w-full md:w-96 bg-white border-l border-gray-200 flex flex-col md:h-full">
        {/* 勋章选择栏 */}
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">目标勋章</h3>
          {loadError ? (
            <div className="text-sm text-red-500">{loadError}</div>
          ) : badges.length === 0 ? (
            <div className="text-sm text-gray-500">暂无可选勋章</div>
          ) : (
            <>
              <div className="flex space-x-3 overflow-x-auto pb-2">
                {badges.map((badge) => (
                  <button
                    key={badge.id}
                    onClick={() => setSelectedBadge(badge)}
                    className={`flex-shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center text-2xl transition-all border-2 ${
                      selectedBadge?.id === badge.id
                        ? 'border-indigo-500 bg-indigo-50 shadow-md scale-110'
                        : 'border-transparent bg-gray-50 hover:bg-gray-100 grayscale'
                    } ${earnedBadges.includes(badge.id) ? 'grayscale-0 border-green-400' : ''}`}
                  >
                    {badge.icon}
                    {earnedBadges.includes(badge.id) && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white" />
                    )}
                  </button>
                ))}
              </div>
              {selectedBadge ? (
                <div className="mt-4 p-4 bg-indigo-50 rounded-xl">
                  <h4 className="font-bold text-indigo-900">{selectedBadge.name}</h4>
                  <p className="text-sm text-indigo-700 mt-1">{selectedBadge.description}</p>
                </div>
              ) : (
                <div className="mt-4 text-sm text-gray-500">请选择一个勋章</div>
              )}
            </>
          )}
        </div>

        {/* 探险智多星反馈区域 */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider mb-4">{AI_NAME}反馈</h3>
          
          <AnimatePresence mode="wait">
            {feedback ? (
              <motion.div
                key="feedback"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="space-y-4"
              >
                {feedback.passed ? (
                  <div className="p-4 bg-green-100 text-green-800 rounded-xl border border-green-200">
                    <div className="font-bold text-lg mb-1">🎉 挑战成功！</div>
                    <p>{feedback.encouragement}</p>
                  </div>
                ) : (
                  <div className="p-4 bg-orange-100 text-orange-800 rounded-xl border border-orange-200">
                    <div className="font-bold text-lg mb-1">💪 加油，还差一点点！</div>
                    <p>{feedback.encouragement}</p>
                  </div>
                )}

                <div className="bg-white p-4 rounded-xl shadow-sm">
                  <h4 className="font-bold text-gray-800 mb-2 flex items-center">
                    <span className="text-green-500 mr-2">✓</span> 亮点
                  </h4>
                  <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                    {feedback.strengths.map((s, i) => (
                      <li key={i}>{s}</li>
                    ))}
                  </ul>
                </div>

                {feedback.improvements.length > 0 && (
                  <div className="bg-white p-4 rounded-xl shadow-sm">
                    <h4 className="font-bold text-gray-800 mb-2 flex items-center">
                      <span className="text-orange-500 mr-2">⚡</span> 建议改进
                    </h4>
                    <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                      {feedback.improvements.map((s, i) => (
                        <li key={i}>{s}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {feedback.examples && feedback.examples.length > 0 && (
                   <div className="bg-white p-4 rounded-xl shadow-sm">
                    <h4 className="font-bold text-gray-800 mb-2 flex items-center">
                      <span className="text-blue-500 mr-2">💡</span> 精彩引用
                    </h4>
                    {feedback.examples.map((ex, i) => (
                      <div key={i} className="mb-2 last:mb-0 text-sm">
                        <div className="italic text-gray-500 border-l-2 border-gray-300 pl-2 mb-1">{`"${ex.quote}"`}</div>
                        <div className="text-gray-700">{ex.comment}</div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            ) : (
              <div className="text-center text-gray-400 mt-10">
                <div className="text-4xl mb-2">🤖</div>
                <p>写一段话，然后点击提交<br/>{AI_NAME}会给你即时建议哦</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};
